<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import { replay, undo, type Turn } from "./domain/rules.js";
import { celebrationFor, isRare, type Celebration } from "./domain/celebrate.js";
import { quickScores } from "./domain/quickscores.js";
import {
  addGroup,
  addPlayer,
  archiveGame,
  emptyState,
  nameIndex,
  removeGroup,
  renameGroup,
  deleteHistoricGame,
  deletedGames,
  discardGame,
  liveGames,
  purgeExpired,
  restoreGame,
  removePlayer,
  renamePlayer,
  setCelebrations,
  startGame,
  summarise,
  type State,
} from "./domain/store.js";
import { exportState, importInto } from "./domain/portability.js";
import { SyncQueue, localQueueStorage } from "./sync/queue.js";
import { httpTransport } from "./sync/transport.js";
import { Session, localSessionStore } from "./sync/session.js";
import { mergePulled, pullEverything } from "./sync/pull.js";
import type { SyncStatus } from "./sync/types.js";
import GroupsScreen from "./features/groups/GroupsScreen.vue";
import SetupScreen from "./features/setup/SetupScreen.vue";
import ScoringScreen from "./features/scoring/ScoringScreen.vue";
import ResultScreen from "./features/result/ResultScreen.vue";
import StatsScreen from "./features/stats/StatsScreen.vue";
import HistoryScreen from "./features/history/HistoryScreen.vue";
import RulesScreen from "./features/rules/RulesScreen.vue";
import SyncBadge from "./features/sync/SyncBadge.vue";
import CelebrationOverlay from "./features/scoring/CelebrationOverlay.vue";

/**
 * Shell and navigation.
 *
 * Groups -> setup -> scoring -> result, with statistics hanging off a group.
 * All of it runs from local state (PRD 8.2): a group, its players and a whole
 * evening's turns are created and usable with no network, and the sync layer
 * mirrors them to the server whenever it can.
 */

const STORE = "kniffel.state";

type View =
  | "groups"
  | "setup"
  | "scoring"
  | "result"
  | "stats"
  | "history"
  | "allTime"
  | "rules";

const state = ref<State>(emptyState());
const view = ref<View>("groups");
const openGroupId = ref<string | null>(null);
const celebration = ref<Celebration | null>(null);
const notice = ref<string | null>(null);
// Device preference (not per group): hold a screen wake lock while scoring.
// Default on -- most people want the table view to stay lit -- and remembered
// per device.
const keepAwake = ref(readKeepAwake());
function readKeepAwake(): boolean {
  try {
    return globalThis.localStorage?.getItem("kniffel.keepAwake") !== "0";
  } catch {
    return true;
  }
}
function onToggleKeepAwake(on: boolean): void {
  keepAwake.value = on;
  try {
    globalThis.localStorage?.setItem("kniffel.keepAwake", on ? "1" : "0");
  } catch {
    // Preference just will not persist; the session still honours it.
  }
}
const status = reactive<SyncStatus>({ online: true, syncing: false, pending: 0, rejected: 0 });
let celebrationTimer: ReturnType<typeof setTimeout> | undefined;

const sessionStore = localSessionStore();
const session = new Session({
  store: sessionStore,
  // One account per device on first run. The server supports pairing a second
  // device onto it; that UI does not exist yet.
  username: () => `kniffel-${Math.random().toString(36).slice(2, 10)}`,
});
const queue = new SyncQueue(localQueueStorage(), httpTransport({ token: () => session.token }));
queue.subscribe((s) => Object.assign(status, s));

const groups = computed(() => summarise(state.value));
const names = computed(() => nameIndex(state.value));
const openGroup = computed(
  () => state.value.groups.find((g) => g.id === openGroupId.value) ?? null,
);
const game = computed(() => state.value.game);
const groupGames = computed(() =>
  liveGames(state.value).filter((g) => g.groupId === openGroupId.value),
);
// The quick-entry buttons learn from the group's own finished games, plus the
// one in progress so a long evening starts adapting within itself.
const quick = computed(() =>
  quickScores([...groupGames.value, ...(state.value.game ? [state.value.game] : [])]),
);
const binnedGames = computed(() =>
  deletedGames(state.value).filter((g) => g.groupId === openGroupId.value),
);

function persist(): void {
  try {
    localStorage.setItem(STORE, JSON.stringify(state.value));
  } catch {
    // Quota or private mode: the evening still works from memory, which beats
    // refusing to record the turn.
  }
}

function load(): void {
  try {
    const raw = localStorage.getItem(STORE);
    if (!raw) return;
    state.value = { ...emptyState(), ...(JSON.parse(raw) as State) };
  } catch {
    state.value = emptyState();
  }
  // 8.3: anything past its thirty days goes now, once, on load.
  state.value = purgeExpired(state.value, Date.now());

  // Reopening mid-game should land where the evening was left off.
  const g = state.value.game;
  if (g) {
    openGroupId.value = g.groupId;
    view.value = replay(g.order, g.turns).finished ? "result" : "scoring";
  }
}

function commit(next: State): void {
  state.value = next;
  persist();
}

// ---------------------------------------------------------------- groups --

function onCreateGroup(name: string): void {
  commit(addGroup(state.value, name, Date.now()));
}

function onRemoveGroup(groupId: string): void {
  commit(removeGroup(state.value, groupId));
  if (openGroupId.value === groupId) openGroupId.value = null;
}

function openGroupView(groupId: string): void {
  openGroupId.value = groupId;
  // Walking into a group that already has a game in progress resumes it.
  view.value = state.value.game?.groupId === groupId ? "scoring" : "setup";
}

function onAddPlayer(name: string): void {
  if (openGroupId.value) commit(addPlayer(state.value, openGroupId.value, name, Date.now()));
}

function onRemovePlayer(playerId: string): void {
  if (openGroupId.value) commit(removePlayer(state.value, openGroupId.value, playerId));
}

function onRenameGroup(name: string): void {
  const id = openGroupId.value;
  if (!id) return;
  commit(renameGroup(state.value, id, name));
  // Push it too, or a restored device would come back with the old name.
  void session.renameGroup(id, name);
}

/** FR-1.4: retroactive, because games hold the id and never the name. */
function onRenamePlayer(playerId: string, name: string): void {
  commit(renamePlayer(state.value, playerId, name));
}

function onSetCelebrations(mode: "all" | "rare"): void {
  if (openGroupId.value) commit(setCelebrations(state.value, openGroupId.value, mode));
}

/** FR-5.4: discarding an unfinished game must not touch the statistics. */
function onDiscard(): void {
  commit(discardGame(state.value));
  view.value = "setup";
}

/** FR-8.1: everything the app knows, as one JSON file. */
function onExport(): void {
  const blob = new Blob([JSON.stringify(exportState(state.value, Date.now()), null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `kniffel-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/** FR-8.2: merge by group id. */
async function onImport(file: File): Promise<void> {
  try {
    const result = importInto(state.value, JSON.parse(await file.text()));
    commit(result.state);
    flash(
      result.groupsAdded + result.groupsReplaced === 0
        ? "Keine Runden in der Datei gefunden."
        : `${result.groupsAdded} neu, ${result.groupsReplaced} ersetzt, ` +
          `${result.gamesImported} ${result.gamesImported === 1 ? "Spiel" : "Spiele"} geladen.`,
    );
  } catch {
    flash("Datei konnte nicht gelesen werden.");
  }
}

// ------------------------------------------------------------------ play --

function onStart(order: string[]): void {
  if (!openGroupId.value) return;
  commit(startGame(state.value, openGroupId.value, order, Date.now()));
  view.value = "scoring";
  void sync();
}

/** Show a celebration briefly; a new one replaces the old rather than queueing. */
function announce(next: Celebration | null): void {
  if (!next) return;
  clearTimeout(celebrationTimer);
  celebration.value = next;
  celebrationTimer = setTimeout(() => (celebration.value = null), 1700);
}

function record(turn: Turn): void {
  const g = state.value.game;
  if (!g) return;
  const seq = g.turns.length;

  // 6.4: decide what to announce from the state either side of the turn.
  const before = replay(g.order, g.turns);
  const after = replay(g.order, [...g.turns, turn]);
  const earned = celebrationFor(before, after, turn, names.value[turn.playerId] ?? turn.playerId);
  // Section 7: a group can ask for only the rare ones.
  const quiet = openGroup.value?.celebrations === "rare";
  announce(earned && (!quiet || isRare(earned.kind)) ? earned : null);

  g.turns.push(turn);
  persist();
  if (after.finished) view.value = "result";

  queue.enqueue(g.id, {
    type: turn.type,
    points: turn.points,
    // Stable per game and position, so a replay after a crash is the same turn
    // to the server rather than a duplicate.
    clientId: `${g.id}:${seq}`,
    seq,
  });
  void sync();
}

const currentPlayer = computed<string | null>(() => {
  const g = state.value.game;
  if (!g || g.order.length === 0) return null;
  return g.order[g.turns.length % g.order.length] ?? null;
});

function onBank(points: number): void {
  const who = currentPlayer.value;
  if (who) record({ playerId: who, type: "bank", points });
}

function onStrike(): void {
  const who = currentPlayer.value;
  if (who) record({ playerId: who, type: "strike", points: 0 });
}

function onUndo(): void {
  const g = state.value.game;
  if (!g) return;
  g.turns = undo(g.turns);
  // FR-3.8: undo can reopen a game that had just finished.
  view.value = "scoring";
  celebration.value = null;
  persist();
}

/** FR-4.4: same people, same order, fresh log. */
function onRematch(): void {
  const g = state.value.game;
  if (!g) return;
  const { order, groupId } = g;
  commit(startGame(archiveGame(state.value, Date.now()), groupId, order, Date.now()));
  view.value = "scoring";
  void sync();
}

/** FR-5.3: a deleted game leaves every statistic that was built from it. */
function onRestoreGame(gameId: string): void {
  commit(restoreGame(state.value, gameId));
}

function onRemoveGame(gameId: string): void {
  commit(deleteHistoricGame(state.value, gameId, Date.now()));
}

/** Done with this game: archive it and go back to the group. */
function onFinish(): void {
  commit(archiveGame(state.value, Date.now()));
  view.value = "setup";
}

// ------------------------------------------------------------------ sync --

/**
 * Get the game onto the server, then send its turns. The game may have been
 * played entirely offline, so the server learns the group, its players and the
 * game itself here, and the queued turns are repointed at the id it returns.
 */
let syncing: Promise<void> | null = null;

/**
 * Reconcile then flush, one at a time. Serialising matters: record() fires this
 * on every turn, so on reconnect several calls land together -- and one flushing
 * turns while another is still creating their game on the server posted them to
 * an id it did not have yet, which came back 404 and rejected them.
 */
function sync(): Promise<void> {
  if (syncing) return syncing;
  syncing = syncOnce().finally(() => {
    syncing = null;
  });
  return syncing;
}

async function syncOnce(): Promise<void> {
  // Every game with turns still waiting needs its server id before those turns
  // can be sent -- not just the game being played. A game that finished offline
  // is archived to history and is no longer state.game, so reconciling only the
  // current game left its turns posting to a local id the server never had.
  // The server answered 404, the turns were rejected, and they piled up as
  // "N Eintrag abgelehnt". reconcile() short-circuits on a game it has already
  // mapped, so revisiting synced games each time is cheap.
  const games = [...state.value.history, ...(state.value.game ? [state.value.game] : [])];
  for (const game of games) {
    if (queue.pending().every((e) => e.gameId !== game.id)) continue;
    const group = state.value.groups.find((x) => x.id === game.groupId);
    if (!group) continue;
    const serverId = await session.reconcile({
      localGameId: game.id,
      localGroupId: group.id,
      order: game.order,
      names: names.value,
      groupName: group.name,
    });
    if (serverId) queue.remapGame(game.id, serverId);
  }
  await queue.flush();
}

/**
 * Fetch what the server holds and fold it in (D-6, additive half).
 *
 * Only ever adds. Called by hand, and automatically on a device that has no
 * groups at all -- a replaced phone or cleared storage, which is the case where
 * the data exists on the server and is otherwise unreachable.
 */
async function restore(announceNothing = true): Promise<void> {
  const pulled = await pullEverything({ token: () => session.token });
  if (!pulled) {
    if (announceNothing) flash("Keine Verbindung zum Server.");
    return;
  }
  const { state: merged, report } = mergePulled(state.value, pulled);
  commit(merged);
  const total = report.groupsAdded + report.gamesAdded + report.playersAdded;
  if (total === 0) {
    if (announceNothing) flash("Alles schon auf diesem Gerät.");
    return;
  }
  flash(
    `${report.groupsAdded} ${report.groupsAdded === 1 ? "Runde" : "Runden"}, ` +
      `${report.gamesAdded} ${report.gamesAdded === 1 ? "Spiel" : "Spiele"} geladen.`,
  );
}

function flash(message: string): void {
  notice.value = message;
  setTimeout(() => (notice.value = null), 4000);
}

/** Put the rejected pile back in the queue and try again. */
async function onRetryRejected(): Promise<void> {
  const n = queue.retryRejected();
  if (n === 0) return;
  flash(`${n} ${n === 1 ? "Eintrag" : "Einträge"} werden erneut gesendet …`);
  await sync();
}

/** Give up on the rejected pile for good. */
function onClearRejected(): void {
  const n = queue.clearRejected();
  if (n > 0) flash(`${n} ${n === 1 ? "Eintrag" : "Einträge"} verworfen.`);
}

const onOnline = (): void => void sync();

onMounted(() => {
  load();
  globalThis.addEventListener("online", onOnline);
  void sync();
  // A device with nothing on it is the one case where the server holds data
  // that is otherwise unreachable, so pull without being asked.
  if (state.value.groups.length === 0 && session.token) void restore(false);
});
onUnmounted(() => {
  globalThis.removeEventListener("online", onOnline);
  clearTimeout(celebrationTimer);
});
</script>

<template>
  <div class="app">
    <GroupsScreen
      v-if="view === 'groups'"
      :groups="groups"
      :rejected="status.rejected"
      @create="onCreateGroup"
      @open="openGroupView"
      @remove="onRemoveGroup"
      @export-data="onExport"
      @import-data="onImport"
      @all-time="view = 'allTime'"
      @rules="view = 'rules'"
      @restore="restore()"
      @retry-rejected="onRetryRejected"
      @clear-rejected="onClearRejected"
    />

    <SetupScreen
      v-else-if="view === 'setup' && openGroup"
      :group-name="openGroup.name"
      :roster="openGroup.players"
      :celebrations="openGroup.celebrations ?? 'all'"
      :has-live-game="game !== null && game.groupId === openGroup.id"
      :keep-awake="keepAwake"
      @start="onStart"
      @add-player="onAddPlayer"
      @remove-player="onRemovePlayer"
      @stats="view = 'stats'"
      @history="view = 'history'"
      @rename-player="onRenamePlayer"
      @rename-group="onRenameGroup"
      @set-celebrations="onSetCelebrations"
      @toggle-keep-awake="onToggleKeepAwake"
      @discard="onDiscard"
      @back="view = 'groups'"
    />

    <ScoringScreen
      v-else-if="view === 'scoring' && game"
      :order="game.order"
      :names="names"
      :turns="game.turns"
      :status="status"
      :quick="quick"
      :keep-awake="keepAwake"
      @bank="onBank"
      @strike="onStrike"
      @undo="onUndo"
      @back="view = 'setup'"
    />

    <ResultScreen
      v-else-if="view === 'result' && game"
      :order="game.order"
      :names="names"
      :turns="game.turns"
      :status="status"
      @rematch="onRematch"
      @close="onFinish"
    />

    <StatsScreen
      v-else-if="view === 'stats' && openGroup"
      :title="openGroup.name"
      :games="groupGames"
      :names="names"
      :player-ids="openGroup.players.map((p) => p.id)"
      @back="view = 'setup'"
    />

    <!-- FR-7.1: every finished game, across every group. Players are matched
         by identity, never by name -- the same StatsScreen, wider input. -->
    <RulesScreen
      v-else-if="view === 'rules'"
      @back="view = 'groups'"
    />

    <StatsScreen
      v-else-if="view === 'allTime'"
      title="Ewige Tabelle"
      :games="liveGames(state)"
      :names="names"
      :player-ids="state.groups.flatMap((g) => g.players.map((p) => p.id))"
      @back="view = 'groups'"
    />

    <HistoryScreen
      v-else-if="view === 'history' && openGroup"
      :title="openGroup.name"
      :games="groupGames"
      :binned="binnedGames"
      :names="names"
      @remove="onRemoveGame"
      @restore="onRestoreGame"
      @back="view = 'setup'"
    />

    <div
      v-if="view === 'groups'"
      class="sync-slot"
    >
      <SyncBadge :status="status" />
    </div>

    <div
      v-if="notice"
      class="notice"
    >
      {{ notice }}
    </div>

    <!-- Outside the chain: it floats over whichever screen is showing. -->
    <CelebrationOverlay :celebration="celebration" />
  </div>
</template>

<style scoped>
.app {
  height: 100%;
  position: relative;
}

.notice {
  position: fixed;
  left: 50%;
  bottom: calc(var(--safe-bottom) + 56px);
  transform: translateX(-50%);
  z-index: 30;
  background: var(--accent);
  color: #fff;
  font-size: 13px;
  padding: 9px 14px;
  border-radius: 999px;
  max-width: 90vw;
  text-align: center;
}

.sync-slot {
  position: fixed;
  bottom: calc(var(--safe-bottom) + 10px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  pointer-events: none;
}
</style>
